// Phase 36 Batch T — Admin Analytics > Event Feed.
//
// Global event log, paginated and filterable by event_type / user_id.

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, ListTree, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";

const EVENT_TYPES = [
  { value: "all", label: "All event types" },
  { value: "signup", label: "Signup" },
  { value: "login", label: "Login" },
  { value: "deal_created", label: "Deal created" },
  { value: "ai_analysis", label: "AI analysis" },
  { value: "report_downloaded", label: "Report downloaded" },
  { value: "projection_downloaded", label: "Projection downloaded" },
  { value: "stage_changed", label: "Stage changed" },
  { value: "counter_offer_set", label: "Counter offer set" },
];

const TYPE_COLOR = {
  signup: "bg-emerald-50 text-emerald-700 border-emerald-200",
  login: "bg-indigo-50 text-indigo-700 border-indigo-200",
  deal_created: "bg-amber-50 text-amber-700 border-amber-200",
  ai_analysis: "bg-violet-50 text-violet-700 border-violet-200",
  report_downloaded: "bg-sky-50 text-sky-700 border-sky-200",
  projection_downloaded: "bg-sky-50 text-sky-700 border-sky-200",
  stage_changed: "bg-rose-50 text-rose-700 border-rose-200",
  counter_offer_set: "bg-orange-50 text-orange-700 border-orange-200",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminEvents() {
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [typeFilter, setTypeFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("");
  const [userFilterDeferred, setUserFilterDeferred] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Debounce the free-text user filter so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setUserFilterDeferred(userFilter.trim()), 250);
    return () => clearTimeout(t);
  }, [userFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (typeFilter && typeFilter !== "all") qs.set("event_type", typeFilter);
    if (userFilterDeferred) qs.set("user_id", userFilterDeferred);
    api
      .get(`/admin/events?${qs.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, limit, typeFilter, userFilterDeferred]);

  const totalPages = useMemo(() => {
    if (!data?.total) return 1;
    return Math.max(1, Math.ceil(data.total / limit));
  }, [data, limit]);

  return (
    <div className="space-y-6" data-testid="admin-events-feed">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-indigo-600">Analytics · Event feed</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
            Real-time event log
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Every product-meaningful action — newest first. Filter by event type or user ID.
          </p>
        </div>
      </header>

      {/* Filter bar */}
      <Card className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
        <Select
          value={typeFilter}
          onValueChange={(v) => { setTypeFilter(v); setPage(1); }}
        >
          <SelectTrigger className="w-full md:w-56" data-testid="events-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EVENT_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Filter by user_id (paste a UUID)…"
          value={userFilter}
          onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
          className="md:flex-1"
          data-testid="events-user-filter"
        />
        <div className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
          {data ? `${data.total.toLocaleString()} events` : "—"}
        </div>
      </Card>

      {err && (
        <Card className="flex items-center gap-3 border-rose-200 bg-rose-50/60 p-4 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4" /> {err}
        </Card>
      )}

      {/* Event table */}
      <Card className="overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
          <ListTree className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-900">Events</h2>
        </div>
        {loading || !data ? (
          <div className="flex h-48 items-center justify-center text-slate-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading events…
          </div>
        ) : data.items.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-slate-400">
            No events match your filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-2 text-left">When</th>
                  <th className="px-5 py-2 text-left">Event</th>
                  <th className="px-5 py-2 text-left">User</th>
                  <th className="px-5 py-2 text-left">Country</th>
                  <th className="px-5 py-2 text-left">Details</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="whitespace-nowrap px-5 py-2 text-xs tabular-nums text-slate-600">
                      {fmt(e.occurred_at)}
                    </td>
                    <td className="px-5 py-2">
                      <Badge variant="outline" className={`border ${TYPE_COLOR[e.event_type] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {e.event_type}
                      </Badge>
                    </td>
                    <td className="px-5 py-2">
                      <div className="text-slate-800">{e.user_name || "—"}</div>
                      <div className="text-xs text-slate-500">{e.user_email || e.user_id || "—"}</div>
                    </td>
                    <td className="px-5 py-2 text-slate-700">{e.country || "—"}</td>
                    <td className="max-w-md px-5 py-2 text-xs text-slate-500">
                      {Object.keys(e.metadata || {}).length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <code className="block truncate font-mono">
                          {JSON.stringify(e.metadata)}
                        </code>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm">
          <span className="text-slate-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="events-prev-page"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="events-next-page"
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
